import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShieldCheck, Mail, Database, Cookie, UserX, Globe2, Baby, Building2, Trash2 } from 'lucide-react';
import SiteDisclaimer from '@/components/SiteDisclaimer';
import BackOrHomeButton from '@/components/BackOrHomeButton';
import { useAuth } from '@/contexts/AuthContext';
import AccountDeletionModal from '@/components/AccountDeletionModal';
import { Button } from '@/components/ui/button';

/**
 * /privacy-policy
 *
 * Plain-English privacy policy for DiviDrip. Inherits the global
 * <AppHeader /> from App.js and renders <SiteDisclaimer /> at the
 * bottom so the disclaimer + footer links match every other page.
 *
 * Contact email lives in CONTACT_EMAIL (also used by SiteDisclaimer).
 */
const CONTACT_EMAIL = 'dividrip@twylightcrow.com';
const LAST_UPDATED = 'July 3, 2026';

function Section({ icon: Icon, title, children }) {
  return (
    <section className="mb-7 last:mb-0">
      <h2 className="text-base sm:text-lg font-bold mb-2 flex items-center gap-2 text-foreground">
        <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-fuchsia-500 dark:text-fuchsia-400 shrink-0" />
        {title}
      </h2>
      <div className="text-sm leading-relaxed text-foreground/90 space-y-2 [&_p]:break-words [&_li]:break-words">
        {children}
      </div>
    </section>
  );
}

export default function PrivacyPolicy() {
  const { user, setAuthUser } = useAuth();
  const navigate = useNavigate();
  const [deletionOpen, setDeletionOpen] = useState(false);

  const handleAccountDeleted = () => {
    // Auth context cleanup — clear the user, then navigate home.
    if (setAuthUser) setAuthUser(null);
    setDeletionOpen(false);
    navigate('/');
  };

  return (
    <div className="min-h-screen min-h-[100svh] bg-background" data-testid="privacy-policy-page">
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-0">
        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
          <BackOrHomeButton testid="privacy-back-or-home-btn" />
          <span className="text-foreground/80">/ Privacy Policy</span>
        </div>

        <header className="mb-6 pb-4 border-b">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-fuchsia-100 dark:bg-fuchsia-950/40 flex items-center justify-center shrink-0">
              <ShieldCheck className="w-5 h-5 text-fuchsia-600 dark:text-fuchsia-400" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Privacy Policy</h1>
              <p className="text-xs text-muted-foreground mt-0.5">
                Last updated: {LAST_UPDATED} &middot; Operated from Michigan, USA
              </p>
            </div>
          </div>
        </header>

        <p className="text-sm text-foreground/90 mb-6 leading-relaxed">
          DiviDrip (the &quot;Service&quot;) is a free dividend-tracking
          tool published by Twylight Crow at{' '}
          <span className="font-semibold">twylightcrow.com</span>. We
          take your privacy seriously, and the short version of this
          policy is the easiest part to remember:{' '}
          <strong className="text-foreground">
            we collect only what we need to keep your watchlist and
            portfolio working, we never sell or rent your information,
            and we never use it to contact you for marketing.
          </strong>
        </p>

        <Section icon={Database} title="1. What We Collect">
          <p>We only collect the minimum information needed to run your account:</p>
          <ul className="list-disc list-outside pl-5 space-y-1.5">
            <li>
              <span className="font-semibold">Email &amp; password (email signup).</span>{' '}
              When you sign up with email, we store your email address
              and a one-way <em>bcrypt</em> hash of your password. We
              never see, log, or store your plain-text password.
            </li>
            <li>
              <span className="font-semibold">Google profile (Google sign-in).</span>{' '}
              When you sign in with Google, we receive only your email
              address, display name, and profile picture URL from
              Google. We never see your Google password.
            </li>
            <li>
              <span className="font-semibold">App data you create.</span>{' '}
              Anything you save inside DiviDrip — your watchlist,
              positions, transactions, monthly income goal, account
              preferences, personal stock journal entries (text notes
              you write on individual tickers), and any optional
              public-share slug you create. In-app filing-alert
              notifications shown in the bell dropdown are also
              derived from your watched tickers combined with public
              SEC 8-K disclosures — the notifications themselves are
              stored per user so you see the same list next time you
              sign in.
            </li>
            <li>
              <span className="font-semibold">Standard server logs.</span>{' '}
              Basic request data (IP address, timestamp, requested URL)
              kept short-term for security, brute-force protection, and
              debugging.
            </li>
          </ul>
        </Section>

        <Section icon={ShieldCheck} title="2. How We Use It">
          <p>We use your information for one purpose: making the app work for you.</p>
          <ul className="list-disc list-outside pl-5 space-y-1.5">
            <li>Authenticating your account and keeping you signed in.</li>
            <li>Saving your watchlist, portfolio positions, transactions, and goals so they reload when you return.</li>
            <li>Generating your private dashboard, calendar, summaries, and downloadable spreadsheet.</li>
            <li>Detecting and blocking abuse (failed-login spam, brute-force attempts).</li>
          </ul>
          <p className="font-semibold text-foreground">
            We do not use your information to email you marketing
            messages, send promotional notifications, or contact you
            for any reason other than direct support replies if{' '}
            <em>you</em> email us first.
          </p>
        </Section>

        <Section icon={UserX} title="3. We Never Sell or Share Your Data">
          <p>
            We never sell, rent, lease, license, or otherwise share
            your personal information with advertisers, data brokers,
            marketers, or any other third parties. Your data is yours.
          </p>
          <p>
            The only third parties that ever touch your data are the
            service providers we strictly need to run the website (see
            Section 5 below), and we only share the bare minimum needed
            for them to do their job.
          </p>
        </Section>

        <Section icon={Cookie} title="4. Cookies">
          <p>
            We use only the cookies needed to keep you signed in. No
            advertising cookies, no analytics cookies, no tracking
            pixels.
          </p>
          <ul className="list-disc list-outside pl-5 space-y-1.5">
            <li>
              <span className="font-semibold">Session / auth cookies</span> —
              issued when you log in. They tell our server it&apos;s
              still you on each request. Cleared automatically when you
              log out.
            </li>
            <li>
              <span className="font-semibold">Local browser storage</span> —
              we save lightweight preferences and UI state in your
              browser&apos;s <code>localStorage</code> and{' '}
              <code>sessionStorage</code>. This includes your selected
              theme, the last dashboard view you had open, table sort
              orders, whether you prefer Nominal or Real yield display,
              whether DRIP projections render in nominal or today&apos;s
              dollars, one-time onboarding hints you&apos;ve dismissed,
              and (when it&apos;s implemented) the last stock modal tab
              and scroll position you were viewing so a refresh puts
              you back where you were. None of this ever leaves your
              device.
            </li>
            <li>
              <span className="font-semibold">Referral codes.</span>{' '}
              If you arrive via a URL like <code>?ref=SOMECODE</code>{' '}
              (e.g., from a friend sharing DiviDrip), that referral
              code is temporarily stashed in <code>sessionStorage</code>{' '}
              so it survives the signup flow, then submitted with your
              account creation and immediately cleared. We use these
              codes only to understand which sources bring new users;
              they are never shared with advertisers.
            </li>
          </ul>
        </Section>

        <Section icon={Building2} title="5. Service Providers (Sub-processors)">
          <p>
            DiviDrip is hosted on infrastructure provided by{' '}
            <span className="font-semibold">Emergent</span>{' '}
            (emergentagent.com). Emergent acts as our hosting and
            authentication provider:
          </p>
          <ul className="list-disc list-outside pl-5 space-y-1.5">
            <li>The MongoDB database storing your account and portfolio is hosted on Emergent&apos;s infrastructure.</li>
            <li>The Google sign-in flow is bridged through Emergent&apos;s OAuth service before reaching us.</li>
          </ul>
          <p>
            Emergent processes data on our behalf as a technical
            sub-processor and does not use your data for its own
            commercial purposes. If you sign in with Google, Google
            also processes the authentication request under Google&apos;s
            own privacy policy.
          </p>
          <p>
            Public stock data shown in the app comes from Massive,
            Yahoo Finance, and Finnhub. Those providers see
            only the ticker symbols
            we look up — never your account, name, or email.
          </p>
        </Section>

        <Section icon={UserX} title="6. Your Rights — Delete Your Account Anytime">
          <p>
            You can permanently delete your account at any time. When
            you do, we hard-delete <strong>everything</strong> we have
            associated with you:
          </p>
          <ul className="list-disc list-outside pl-5 space-y-1.5">
            <li>Login credentials (email, password hash, or Google profile data)</li>
            <li>Display name and profile picture</li>
            <li>Watchlist entries</li>
            <li>Portfolio positions and full transaction history</li>
            <li>Monthly income goal and any saved preferences</li>
            <li>Any public-share slug you created (the share link stops working)</li>
            <li>Active session tokens (you&apos;ll be signed out everywhere)</li>
          </ul>
          <p>
            To delete your account, email us at{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-sky-700 dark:text-sky-300 hover:underline" data-testid="privacy-delete-email">
              {CONTACT_EMAIL}
            </a>{' '}
            from the email address on file. You may also request a copy
            of the data we hold on you, or ask us to correct anything
            inaccurate, at the same address.
          </p>

          {/* Self-service hard-delete — visible only to logged-in users.
              Logged-out visitors still see the email-based path above. */}
          {user && (
            <div
              className="mt-3 rounded-lg border-2 border-red-300 dark:border-red-800/70 bg-red-50/60 dark:bg-red-950/30 p-4"
              data-testid="privacy-self-delete-block"
            >
              <p className="text-sm font-semibold text-red-900 dark:text-red-200 mb-1">
                Prefer to do it yourself? You can hard-delete your account right now.
              </p>
              <p className="text-xs text-red-800/90 dark:text-red-300/90 mb-3 leading-relaxed">
                Signed in as <span className="font-semibold break-all">{user.email}</span>. The button below will purge every record listed above from our database. There is no recovery and no undo.
              </p>
              <Button
                type="button"
                variant="destructive"
                className="gap-1.5"
                onClick={() => setDeletionOpen(true)}
                data-testid="privacy-delete-account-btn"
              >
                <Trash2 className="w-4 h-4" />
                Delete My Account
              </Button>
            </div>
          )}
        </Section>

        <Section icon={Database} title="7. Data Retention">
          <p>
            While your account is active, we keep your account and app
            data so the Service works for you. If you delete your
            account, all of it is removed immediately as described in
            Section 6 above. Server access logs are kept short-term for
            security and debugging, then rotated out.
          </p>
        </Section>

        <Section icon={Baby} title="8. Children — 18+ Only">
          <p>
            DiviDrip is intended for adults 18 years of age or older.
            Most U.S. brokerages require investors to be 18+ to open
            self-directed accounts, and our content assumes the reader
            can legally invest. We do not knowingly collect information
            from anyone under 18. If you believe a minor has created an
            account, contact us at{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-sky-700 dark:text-sky-300 hover:underline">{CONTACT_EMAIL}</a>{' '}
            and we&apos;ll delete it.
          </p>
        </Section>

        <Section icon={ShieldCheck} title="9. Security">
          <p>
            Passwords are stored only as one-way bcrypt hashes — even
            we cannot read them. Sessions are issued as HTTP-only,
            secure cookies (so they can&apos;t be read by JavaScript or
            sent over plain HTTP). All traffic to and from the site is
            served over HTTPS. We follow standard practices to keep
            your data safe, but no system on the internet is 100%
            secure, so we encourage you to use a unique password and
            log out from shared devices.
          </p>
        </Section>

        <Section icon={Globe2} title="10. International Users">
          <p>
            DiviDrip is operated from Michigan, USA. If you access the
            Service from outside the United States, you understand and
            agree that your information will be processed in the U.S.,
            which may have data-protection laws different from those of
            your home country.
          </p>
        </Section>

        <Section icon={ShieldCheck} title="11. Changes to This Policy">
          <p>
            If we materially change this policy we&apos;ll update the
            &quot;Last updated&quot; date at the top of this page. For
            substantive changes that affect how we use your data,
            we&apos;ll post a notice in the app the next time you log
            in.
          </p>
        </Section>

        <Section icon={Mail} title="12. Contact Us">
          <p>
            Questions, deletion requests, or anything else privacy-related:
          </p>
          <p>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="inline-flex items-center gap-1.5 text-sky-700 dark:text-sky-300 hover:underline font-semibold"
              data-testid="privacy-contact-email"
            >
              <Mail className="w-4 h-4" />
              {CONTACT_EMAIL}
            </a>
          </p>
          <p className="text-xs text-muted-foreground">
            Twylight Crow &middot; Michigan, USA
          </p>
        </Section>
      </main>

      <SiteDisclaimer />

      <AccountDeletionModal
        open={deletionOpen}
        onClose={() => setDeletionOpen(false)}
        onDeleted={handleAccountDeleted}
        userEmail={user?.email}
      />
    </div>
  );
}
